import React, { useState } from 'react';
import { Button, Input, Modal } from 'semantic-ui-react';
import { Globe, Loader2, Camera } from 'lucide-react';
import { useFluidFormClient } from '../fluidForm.client';
export const URLCapture = ({ onURLCapture, background = 'white' }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [url, setUrl] = useState('');
    const [isCapturing, setIsCapturing] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [capturedImage, setCapturedImage] = useState(null);
    const client = useFluidFormClient();
    const handleCapture = async () => {
        if (!url.trim()) {
            setError('Please enter a valid URL');
            return;
        }
        // Basic URL validation
        try {
            new URL(url);
        }
        catch {
            setError('Please enter a valid URL (e.g., https://example.com)');
            return;
        }
        setIsCapturing(true);
        setError(null);
        try {
            // Capture the URL as an image
            const imageBlob = await client.captureUrl(url);
            // Convert blob to object URL for preview
            const imageUrl = URL.createObjectURL(imageBlob);
            setCapturedImage(imageUrl);
        }
        catch (error) {
            setError('Failed to capture URL. Please try again.');
        }
        finally {
            setIsCapturing(false);
        }
    };
    const handleProcess = async () => {
        if (!capturedImage)
            return;
        setIsProcessing(true);
        setError(null);
        try {
            await onURLCapture(url);
            handleClose();
        }
        catch (error) {
            console.error('Error processing URL:', error);
            setError('Failed to process URL. Please try again.');
        }
        finally {
            setIsProcessing(false);
        }
    };
    const handleClose = () => {
        setIsModalOpen(false);
        setUrl('');
        setError(null);
        if (capturedImage) {
            URL.revokeObjectURL(capturedImage);
            setCapturedImage(null);
        }
    };
    let backgroundClass = background === 'white' ? 'bg-white' : 'bg-gray-900';
    let textColor = background === 'white' ? 'text-gray-600' : 'text-white';
    return (React.createElement(React.Fragment, null,
        React.createElement("button", { onClick: () => setIsModalOpen(true), disabled: isCapturing || isProcessing, className: `w-10 h-10 rounded-full ${backgroundClass} border border-gray-200 hover:border-orange-300 hover:bg-orange-50 flex items-center justify-center transition-all duration-300 group shadow-sm ${(isCapturing || isProcessing) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`, title: (isCapturing || isProcessing) ? "Processing..." : "Capture website screenshot" }, (isCapturing || isProcessing) ? (React.createElement(Loader2, { className: `w-5 h-5 ${textColor} animate-spin` })) : (React.createElement(Globe, { className: `w-5 h-5 ${textColor} group-hover:text-orange-500` }))),
        React.createElement(Modal, { open: isModalOpen, onClose: handleClose, size: capturedImage ? "large" : "small", closeIcon: true },
            React.createElement(Modal.Header, null,
                React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
                    React.createElement(Globe, { size: 16 }),
                    "Capture Website Screenshot")),
            React.createElement(Modal.Content, null, !capturedImage ? (React.createElement(React.Fragment, null,
                React.createElement("p", null, "Enter a website URL to capture a screenshot and extract form data:"),
                React.createElement(Input, { fluid: true, placeholder: "https://example.com", value: url, onChange: (e) => setUrl(e.target.value), error: !!error, onKeyPress: (e) => {
                        if (e.key === 'Enter') {
                            handleCapture();
                        }
                    } }),
                error && (React.createElement("div", { style: { color: 'red', marginTop: '8px', fontSize: '0.9em' } }, error)))) : (React.createElement(React.Fragment, null,
                React.createElement("p", null, "Screenshot captured successfully! Review the image below and click \"Process\" to extract form data:"),
                React.createElement("div", { style: { marginTop: '12px', fontSize: '0.9em', color: '#666' } },
                    React.createElement("strong", null, "URL:"),
                    " ",
                    url),
                React.createElement("div", { style: {
                        marginTop: '16px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        maxHeight: '500px',
                        overflowY: 'auto',
                        backgroundColor: '#f9f9f9'
                    } },
                    React.createElement("img", { src: capturedImage, alt: "Website screenshot", style: {
                            width: '100%',
                            height: 'auto',
                            display: 'block'
                        } })),
                error && (React.createElement("div", { style: { color: 'red', marginTop: '8px', fontSize: '0.9em' } }, error))))),
            React.createElement(Modal.Actions, null,
                React.createElement(Button, { onClick: handleClose }, "Cancel"),
                !capturedImage ? (React.createElement(Button, { color: "blue", loading: isCapturing, disabled: !url.trim() || isCapturing, onClick: handleCapture },
                    React.createElement(Camera, { size: 14, style: { marginRight: '8px' } }),
                    "Capture")) : (React.createElement(React.Fragment, null,
                    React.createElement(Button, { onClick: () => {
                            if (capturedImage) {
                                URL.revokeObjectURL(capturedImage);
                                setCapturedImage(null);
                            }
                            setError(null);
                        } }, "Retake"),
                    React.createElement(Button, { color: "green", loading: isProcessing, disabled: isProcessing, onClick: handleProcess },
                        React.createElement(Camera, { size: 14, style: { marginRight: '8px' } }),
                        "Process")))))));
};
export default URLCapture;
